import { type FieldPolicy, InMemoryCache, makeVar } from '@apollo/client';
import type { KeySpecifier } from '@apollo/client/cache/inmemory/policies';
import type { Reference } from '@apollo/client/utilities';

import type { DealFormState, DealState } from '@openx/types/deal';
import type { PackageFormState } from '@openx/types/package';

type KeyArgs = FieldPolicy<unknown>['keyArgs'];

export function openAudiencePagination<T = Reference>(keyArgsAlias = 'NaN'): FieldPolicy<T[]> {
  const keyArgs: KeyArgs = (_args, context): KeySpecifier => {
    const keys: KeySpecifier = [];
    if (context?.field?.alias?.value === keyArgsAlias) {
      return (
        // False positive
        // eslint-disable-next-line baseline-js/use-baseline
        context.field?.arguments?.map(arg => arg.name.value).filter(arg => arg !== 'limit' && arg !== 'offset') ?? []
      );
    }

    // False positive
    // eslint-disable-next-line baseline-js/use-baseline
    const res: KeySpecifier = [...(context.field?.arguments?.map(arg => arg.name.value) ?? [])];
    return res.length > 0 ? res : keys || (false as const);
  };

  //Merge function to handle pagination
  const merge = (existing, incoming, { args }) => {
    // const merge = (existing, incoming, { args, field, ...other }) => {

    const merged = existing ? existing.slice(0) : [];

    if (incoming) {
      if (args) {
        // Assume an offset of 0 if args.offset omitted.
        const { offset = 0 } = args;
        for (let i = 0; i < incoming.length; ++i) {
          const matchIdx = merged.map(e => e?.__ref).indexOf(incoming[i]?.__ref);
          if (matchIdx !== -1) {
            merged[matchIdx] = undefined;
          }

          merged[offset + i] = incoming[i];
        }
      } else {
        merged.push(...incoming);
      }
    }

    return merged;
  };

  //TODO UIG-540 Read function to handle where filters and remove custom key args for dataFeed
  // const read = (existing, { args, ..._other }) => {
  /*
  Returning undefined triggers apollo to make network call,
  while [] will be cache response without network
  */
  const read = (existing, { args, field }) => {
    if (keyArgsAlias !== field?.alias?.value) return existing;

    const { offset = 0, limit = 0 } = args || {};
    if (!existing || existing.length < offset) {
      return;
    }
    const slice = offset + limit > existing.length ? existing.length : offset + limit;

    const page = existing?.slice(offset, slice);

    if (page.includes(undefined) || page.length === 0) return;

    return page;
  };

  return {
    keyArgs,
    merge,
    read,
  };
}

function auditTrails<T = Reference>(): FieldPolicy<T[]> {
  const keyArgs: KeyArgs = (_args, context): KeySpecifier => {
    const keys: KeySpecifier = [];
    const res = [
      // False positive
      // eslint-disable-next-line baseline-js/use-baseline
      ...(context.field?.arguments?.map(arg => arg.name.value).filter(arg => arg !== 'limit' && arg !== 'offset') ??
        []),
    ];
    return res.length > 0 ? res : keys || (false as const);
  };

  const merge = (existing, incoming) => {
    const merged = existing ? structuredClone(existing) : {};
    merged.objects = merged.objects ?? [];

    if (incoming) {
      merged.has_more = incoming.has_more;
      merged.limit = incoming.limit;
      merged.offset = incoming.offset;

      incoming.objects?.forEach((element, index) => {
        merged.objects[incoming.offset + index] = element;
      });
    }

    return merged;
  };

  return {
    keyArgs,
    merge,
  };
}

type DealNTargeting = DealState & Pick<PackageFormState, 'targeting'>;

const dealForm = <T extends DealState = DealNTargeting>(): DealFormState<T> => ({
  loading: false,
});

// export const dealFormStateFn = <T extends DealState = DealNTargeting>() => makeVar<DealFormState<T>>(dealForm<T>());
// export const dealFormState = dealFormStateFn();
let dealFormStateFn;
export const dealFormState = <T extends DealState = DealNTargeting>() => {
  if (!dealFormStateFn) dealFormStateFn = makeVar<DealFormState<T>>(dealForm<T>());
  return dealFormStateFn;
};

const OptionsByPathFieldFn = (obj, ..._args) => {
  if (obj?.filter) return ['id', 'path', 'filter'];

  return ['id', 'path'];
};

export const cache = new InMemoryCache({
  typePolicies: {
    OptionsByPathResult: {
      keyFields: OptionsByPathFieldFn,
    },
    Query: {
      fields: {
        audience: openAudiencePagination('audienceFeed'),
        deal: openAudiencePagination('dealFeed'),
        lookalike_definition: openAudiencePagination('lalSegmentFeed'),
        auditTrail: auditTrails(),
        getAuditTrails: auditTrails(),
      },
    },
  },
});
